import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  IconButton,
  TextField,
  CircularProgress,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SendIcon from '@mui/icons-material/Send';
import MicIcon from '@mui/icons-material/Mic';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import MoodIcon from '@mui/icons-material/Mood';
import { useCommunity } from '../../contexts/CommunityContext';
import { inferMediaMessageType } from '../../services/communityService';
import { extractFirstUrl, fetchLinkPreview } from '../../services/linkPreviewService';
import type { CommunityMessageLinkPreview } from '../../services/communityService';
import { LinkPreviewDraft } from './LinkPreviewDraft';
import { ReplyPreviewBar } from './ReplyPreviewBar';
import { CommunityAttachMenu } from './CommunityAttachMenu';
import { CommunityCameraModal } from './CommunityCameraModal';
import { CommunityEmojiPicker } from './CommunityEmojiPicker';
import { VoiceRecordingWaveform } from './VoiceRecordingWaveform';
import {
  formatRecordingDuration,
  useVoiceRecorder,
} from '../../hooks/useVoiceRecorder';
import { getCommunitySendFailureMessage } from '../../utils/communityApiErrors';

interface Props {
  canPost: boolean;
  disabled?: boolean;
  onNotify: (message: string) => void;
}

export const CommunityComposerBar: React.FC<Props> = ({ canPost, disabled = false, onNotify }) => {
  const {
    replyTo,
    setReplyTo,
    sendMessage,
    sendMediaMessage,
    sendDocumentMessage,
    sendVoiceMessage,
    sendLocationMessage,
    emitTypingEvent,
    emitStopTypingEvent,
  } = useCommunity();

  const [inputText, setInputText] = useState('');
  const [sending, setSending] = useState(false);
  const [attachOpen, setAttachOpen] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [locationLoading, setLocationLoading] = useState(false);
  const [linkDraft, setLinkDraft] = useState<CommunityMessageLinkPreview | null>(null);
  const [linkDraftLoading, setLinkDraftLoading] = useState(false);
  const linkFetchRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const attachAnchorRef = useRef<HTMLButtonElement>(null);
  const emojiAnchorRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const {
    isRecording,
    isPaused,
    durationMs,
    waveformHistory,
    startRecording,
    pauseRecording,
    resumeRecording,
    stopRecording,
    cancelRecording,
  } = useVoiceRecorder();

  const handleSendText = useCallback(async () => {
    const text = inputText.trim();
    if (!text || sending || disabled) return;
    setInputText('');
    setLinkDraft(null);
    emitStopTypingEvent();
    setSending(true);
    try {
      await sendMessage(text);
    } catch (err) {
      onNotify(getCommunitySendFailureMessage(err));
    } finally {
      setSending(false);
    }
  }, [inputText, sending, disabled, sendMessage, emitStopTypingEvent, onNotify]);

  const handleDocumentFile = useCallback(
    async (file: File) => {
      setSending(true);
      try {
        await sendDocumentMessage(file);
      } catch (err) {
        onNotify(getCommunitySendFailureMessage(err, 'Failed to send document'));
      } finally {
        setSending(false);
      }
    },
    [sendDocumentMessage, onNotify]
  );

  const refreshLinkDraft = useCallback((text: string) => {
    if (linkFetchRef.current) clearTimeout(linkFetchRef.current);
    const url = extractFirstUrl(text);
    if (!url) {
      setLinkDraft(null);
      setLinkDraftLoading(false);
      return;
    }
    setLinkDraftLoading(true);
    linkFetchRef.current = setTimeout(() => {
      void fetchLinkPreview(url).then((preview) => {
        setLinkDraft(preview);
        setLinkDraftLoading(false);
      });
    }, 400);
  }, []);

  useEffect(
    () => () => {
      if (linkFetchRef.current) clearTimeout(linkFetchRef.current);
    },
    []
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void handleSendText();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputText(e.target.value);
    refreshLinkDraft(e.target.value);
    if (e.target.value) emitTypingEvent();
    else emitStopTypingEvent();
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    const pasted = e.clipboardData.getData('text');
    if (pasted) {
      requestAnimationFrame(() => refreshLinkDraft(`${inputText}${pasted}`));
    }
  };

  const handleMediaFiles = useCallback(
    async (files: FileList | File[]) => {
      const list = Array.from(files);
      if (!list.length) return;
      setSending(true);
      try {
        for (const file of list) {
          await sendMediaMessage(file, { message_type: inferMediaMessageType(file) });
        }
      } catch (err) {
        onNotify(getCommunitySendFailureMessage(err, 'Failed to send media'));
      } finally {
        setSending(false);
      }
    },
    [sendMediaMessage, onNotify]
  );

  const handleLocation = useCallback(async () => {
    if (!navigator.geolocation) {
      onNotify('Location is not supported in this browser');
      return;
    }
    setLocationLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          await sendLocationMessage(
            pos.coords.latitude,
            pos.coords.longitude,
            'Shared location'
          );
        } catch (err) {
          onNotify(getCommunitySendFailureMessage(err, 'Failed to send location'));
        } finally {
          setLocationLoading(false);
        }
      },
      () => {
        onNotify('Could not get your location');
        setLocationLoading(false);
      },
      { enableHighAccuracy: true, timeout: 15000 }
    );
  }, [sendLocationMessage, onNotify]);

  const insertEmoji = (emoji: string) => {
    const el = inputRef.current;
    if (!el) {
      setInputText((prev) => prev + emoji);
      return;
    }
    const start = el.selectionStart ?? inputText.length;
    const end = el.selectionEnd ?? inputText.length;
    const next = inputText.slice(0, start) + emoji + inputText.slice(end);
    setInputText(next);
    requestAnimationFrame(() => {
      el.focus();
      const caret = start + emoji.length;
      el.setSelectionRange(caret, caret);
    });
  };

  const handleStartRecording = async () => {
    try {
      await startRecording();
    } catch {
      onNotify('Microphone access denied');
    }
  };

  const handleSendVoice = async () => {
    if (!isRecording || sending) return;
    setSending(true);
    try {
      const result = await stopRecording();
      if (!result || result.durationMs < 400) {
        onNotify('Record a longer voice message');
        return;
      }
      await sendVoiceMessage(result.blob, result.durationMs);
    } catch (err) {
      onNotify(getCommunitySendFailureMessage(err, 'Failed to send voice message'));
    } finally {
      setSending(false);
    }
  };

  const handleCancelRecording = () => {
    cancelRecording();
  };

  const handlePauseToggle = () => {
    if (isPaused) resumeRecording();
    else pauseRecording();
  };

  if (!canPost) {
    return (
      <Box
        sx={{
          px: 2,
          py: 1.5,
          bgcolor: '#f0f2f5',
          borderTop: '1px solid #e8eaed',
          textAlign: 'center',
        }}
      >
        <Typography sx={{ fontSize: '0.82rem', color: '#9aa0a6' }}>
          Only admins can post in this community
        </Typography>
      </Box>
    );
  }

  const hasText = Boolean(inputText.trim());
  const showSend = hasText && !isRecording;

  return (
    <>
      {replyTo && <ReplyPreviewBar replyTo={replyTo} onClear={() => setReplyTo(null)} />}

      <LinkPreviewDraft
        preview={linkDraft}
        loading={linkDraftLoading}
        onDismiss={() => {
          setLinkDraft(null);
          setLinkDraftLoading(false);
        }}
      />

      <Box
        sx={{
          px: 1.25,
          py: 1,
          bgcolor: '#f0f2f5',
          borderTop: '1px solid #e8eaed',
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
        }}
      >
        {!isRecording && (
          <IconButton
            ref={attachAnchorRef}
            onClick={() => setAttachOpen(true)}
            disabled={disabled || sending}
            sx={{ color: '#54656f' }}
            aria-label="Attach"
          >
            <AddIcon />
          </IconButton>
        )}

        {isRecording ? (
          <>
            <IconButton
              onClick={handleCancelRecording}
              disabled={sending}
              sx={{ color: '#54656f' }}
              aria-label="Delete recording"
            >
              <DeleteOutlineIcon />
            </IconButton>

            <Box
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                bgcolor: '#fff',
                borderRadius: 999,
                border: '1px solid #e9edef',
                px: 1.25,
                py: 0.85,
                minHeight: 44,
                boxShadow: '0 1px 0 rgba(0,0,0,0.04)',
              }}
            >
              <Box
                sx={{
                  width: 9,
                  height: 9,
                  borderRadius: '50%',
                  bgcolor: '#ea0038',
                  flexShrink: 0,
                  animation: isPaused ? 'none' : 'recPulse 1.2s ease-in-out infinite',
                  '@keyframes recPulse': {
                    '0%, 100%': { opacity: 1 },
                    '50%': { opacity: 0.35 },
                  },
                }}
              />
              <Typography
                sx={{
                  fontSize: '0.9rem',
                  fontWeight: 500,
                  color: '#111b21',
                  minWidth: 38,
                  flexShrink: 0,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {formatRecordingDuration(durationMs)}
              </Typography>
              <VoiceRecordingWaveform
                levels={waveformHistory}
                active={!isPaused}
                color="rgba(17,27,33,0.72)"
                dotColor="rgba(17,27,33,0.28)"
              />
            </Box>

            <IconButton
              onClick={handlePauseToggle}
              disabled={sending}
              sx={{ color: '#54656f' }}
              aria-label={isPaused ? 'Resume recording' : 'Pause recording'}
            >
              {isPaused ? <PlayArrowIcon /> : <PauseIcon />}
            </IconButton>

            <IconButton
              onClick={() => void handleSendVoice()}
              disabled={disabled || sending}
              sx={{
                bgcolor: '#00a884',
                color: '#fff',
                width: 44,
                height: 44,
                '&:hover': { bgcolor: '#008f72' },
                '&:disabled': { bgcolor: '#8696a0', color: '#fff' },
              }}
              aria-label="Send voice message"
            >
              {sending ? <CircularProgress size={18} color="inherit" /> : <SendIcon sx={{ fontSize: 20 }} />}
            </IconButton>
          </>
        ) : (
          <>
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'flex-end',
                bgcolor: '#fff',
                borderRadius: 999,
                border: '1px solid #e9edef',
                px: 0.5,
                minHeight: 42,
              }}
            >
              <TextField
                inputRef={inputRef}
                fullWidth
                multiline
                maxRows={4}
                placeholder="Type a message…"
                value={inputText}
                onChange={handleInputChange}
                onPaste={handlePaste}
                onKeyDown={handleKeyDown}
                disabled={disabled || sending}
                variant="standard"
                InputProps={{
                  disableUnderline: true,
                  sx: { fontSize: '0.9rem', px: 1.5, py: 1 },
                }}
              />
              <IconButton
                ref={emojiAnchorRef}
                size="small"
                onClick={() => setEmojiOpen(true)}
                disabled={disabled || sending}
                sx={{ color: '#8696a0', mb: 0.25, mr: 0.25 }}
                aria-label="Emoji"
              >
                <MoodIcon fontSize="small" />
              </IconButton>
            </Box>

            {showSend ? (
              <IconButton
                onClick={() => void handleSendText()}
                disabled={disabled || sending}
                sx={{
                  bgcolor: '#00a884',
                  color: '#fff',
                  width: 44,
                  height: 44,
                  '&:hover': { bgcolor: '#008f72' },
                  '&:disabled': { bgcolor: '#8696a0', color: '#fff' },
                }}
                aria-label="Send"
              >
                {sending ? <CircularProgress size={18} color="inherit" /> : <SendIcon sx={{ fontSize: 20 }} />}
              </IconButton>
            ) : (
              <IconButton
                onClick={() => void handleStartRecording()}
                disabled={disabled || sending}
                sx={{ color: '#54656f' }}
                aria-label="Record voice message"
              >
                <MicIcon />
              </IconButton>
            )}
          </>
        )}
      </Box>

      <CommunityAttachMenu
        anchorEl={attachAnchorRef.current}
        open={attachOpen}
        onClose={() => setAttachOpen(false)}
        onGallerySelect={(files) => void handleMediaFiles(files)}
        onOpenCamera={() => setCameraOpen(true)}
        onDocumentSelect={(file) => void handleDocumentFile(file)}
        onLocationSelect={() => void handleLocation()}
        locationLoading={locationLoading}
      />

      <CommunityCameraModal
        open={cameraOpen}
        onClose={() => setCameraOpen(false)}
        onCapture={(file) => void handleMediaFiles([file])}
        onError={onNotify}
      />

      <CommunityEmojiPicker
        anchorEl={emojiAnchorRef.current}
        open={emojiOpen}
        onClose={() => setEmojiOpen(false)}
        onSelect={insertEmoji}
      />
    </>
  );
};
